import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

// Crown Icon Component
const CrownIcon = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M5 16L3 8l2.5 2L12 4l6.5 6L21 8l-2 8H5zm2.5-7L6 8l1 4h10l1-4-1.5 1L12 7 7.5 9z"/>
  </svg>
);

// Mock data for states and counties
const stateData = {
  texas: {
    name: 'Texas',
    counties: [
      { name: 'Anderson', leads: 18 },
      { name: 'Andrews', leads: 14 },
      { name: 'Angelina', leads: 43 },
      { name: 'Atascosa', leads: 27 },
      { name: 'Austin', leads: 31 },
      { name: 'Bailey', leads: 8 },
      { name: 'Bandera', leads: 15 },
      { name: 'Bastrop', leads: 22 },
      { name: 'Baylor', leads: 9 },
      { name: 'Bee', leads: 19 }
    ]
  },
  california: {
    name: 'California',
    counties: [
      { name: 'Los Angeles', leads: 245 },
      { name: 'San Francisco', leads: 89 },
      { name: 'San Diego', leads: 156 },
      { name: 'Orange', leads: 134 },
      { name: 'Riverside', leads: 87 },
      { name: 'Sacramento', leads: 76 },
      { name: 'Alameda', leads: 65 },
      { name: 'Santa Clara', leads: 98 },
      { name: 'Fresno', leads: 54 },
      { name: 'Kern', leads: 43 }
    ]
  },
  florida: {
    name: 'Florida',
    counties: [
      { name: 'Miami-Dade', leads: 187 },
      { name: 'Broward', leads: 123 },
      { name: 'Palm Beach', leads: 98 },
      { name: 'Hillsborough', leads: 87 },
      { name: 'Orange', leads: 76 },
      { name: 'Pinellas', leads: 65 },
      { name: 'Duval', leads: 89 },
      { name: 'Lee', leads: 54 },
      { name: 'Polk', leads: 43 },
      { name: 'Brevard', leads: 38 }
    ]
  },
  newyork: {
    name: 'New York',
    counties: [
      { name: 'Kings', leads: 156 },
      { name: 'Queens', leads: 134 },
      { name: 'New York', leads: 198 },
      { name: 'Suffolk', leads: 87 },
      { name: 'Bronx', leads: 76 },
      { name: 'Nassau', leads: 65 },
      { name: 'Westchester', leads: 54 },
      { name: 'Erie', leads: 43 },
      { name: 'Monroe', leads: 38 },
      { name: 'Richmond', leads: 29 }
    ]
  }
};

// Create Recording Form Component
const CreateRecordingForm = ({ county, state, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    sheriff: 'Sheriff',
    stageName: '',
    callbackNumber: '',
    county: county || '',
    state: state || 'Texas'
  });

  const handleSubmit = () => {
    onSubmit({
      ...formData,
      selectedCounty: county,
      selectedState: state
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-black bg-opacity-75" onClick={onClose}></div>
        
        <div className="inline-block w-full max-w-md my-8 overflow-hidden text-left align-middle transition-all transform bg-gray-800 border border-gray-700 shadow-xl rounded-xl">
          <div className="px-6 py-4 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Create Recording</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white focus:outline-none"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
          
          <div className="px-6 py-6 space-y-4">
            <div>
              <select
                value={formData.sheriff}
                onChange={(e) => setFormData({ ...formData, sheriff: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="Sheriff">Sheriff</option>
                <option value="Deputy">Deputy</option>
                <option value="Officer">Officer</option>
              </select>
            </div>

            <div>
              <input
                type="text"
                placeholder="Stage Name"
                value={formData.stageName}
                onChange={(e) => setFormData({ ...formData, stageName: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <input
                type="tel"
                placeholder="Callback Phone Number"
                value={formData.callbackNumber}
                onChange={(e) => setFormData({ ...formData, callbackNumber: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <input
                type="text"
                placeholder="County"
                value={formData.county}
                onChange={(e) => setFormData({ ...formData, county: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <select
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="Texas">Texas</option>
                <option value="California">California</option>
                <option value="Florida">Florida</option>
                <option value="New York">New York</option>
              </select>
            </div>

            <button
              onClick={handleSubmit}
              className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200"
            >
              Find Agencies
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// County Selection Modal
const CountyModal = ({ isOpen, stateName, counties, onClose, onSelectCounty }) => {
  const [selectedCounty, setSelectedCounty] = useState('');

  if (!isOpen) return null;

  const handleSelectCounty = () => {
    if (selectedCounty) {
      onSelectCounty(selectedCounty);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-black bg-opacity-75" onClick={onClose}></div>
        
        <div className="inline-block w-full max-w-md my-8 overflow-hidden text-left align-middle transition-all transform bg-yellow-600 shadow-xl rounded-xl">
          <div className="px-6 py-4 border-b border-yellow-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-black">{stateName} Counties</h3>
              <button
                onClick={onClose}
                className="text-black hover:text-gray-700 focus:outline-none"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
          
          <div className="px-6 py-6">
            <div className="max-h-64 overflow-y-auto space-y-2 mb-6">
              {counties.map((county) => (
                <label key={county.name} className="flex items-center cursor-pointer p-2 hover:bg-yellow-500 rounded">
                  <input
                    type="radio"
                    name="county"
                    value={county.name}
                    checked={selectedCounty === county.name}
                    onChange={(e) => setSelectedCounty(e.target.value)}
                    className="form-radio h-4 w-4 text-purple-600 focus:ring-purple-500"
                  />
                  <div className="ml-3 flex-1 flex justify-between">
                    <span className="text-black font-medium">{county.name}</span>
                    <span className="text-black">Leads: {county.leads}</span>
                  </div>
                </label>
              ))}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSelectCounty}
                disabled={!selectedCounty}
                className="flex-1 bg-gradient-to-r from-yellow-700 to-yellow-800 hover:from-yellow-800 hover:to-yellow-900 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold py-2 px-4 rounded-lg transition-all duration-200"
              >
                Select County
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// US Map SVG Component (simplified)
const USMapSVG = ({ onStateClick, selectedState }) => {
  const stateCoordinates = {
    texas: { x: 200, y: 280, width: 80, height: 60 },
    california: { x: 50, y: 200, width: 60, height: 80 },
    florida: { x: 350, y: 320, width: 70, height: 40 },
    newyork: { x: 380, y: 120, width: 40, height: 30 }
  };

  return (
    <svg width="500" height="400" viewBox="0 0 500 400" className="w-full h-auto">
      {/* Background */}
      <rect width="500" height="400" fill="#374151" />
      
      {/* States */}
      {Object.entries(stateCoordinates).map(([stateKey, coords]) => (
        <rect
          key={stateKey}
          x={coords.x}
          y={coords.y}
          width={coords.width}
          height={coords.height}
          fill={selectedState === stateKey ? "#d97706" : "#d4af37"}
          stroke="#1f2937"
          strokeWidth="2"
          className="cursor-pointer hover:fill-yellow-500 transition-colors"
          onClick={() => onStateClick(stateKey)}
        />
      ))}
      
      {/* State Labels */}
      <text x="240" y="315" fill="black" fontSize="12" fontWeight="bold" textAnchor="middle">TX</text>
      <text x="80" y="245" fill="black" fontSize="12" fontWeight="bold" textAnchor="middle">CA</text>
      <text x="385" y="345" fill="black" fontSize="12" fontWeight="bold" textAnchor="middle">FL</text>
      <text x="400" y="140" fill="black" fontSize="12" fontWeight="bold" textAnchor="middle">NY</text>
    </svg>
  );
};


import AdminLayoutWrapper from './common/AdminLayoutWrapper';
// Main Content Area
const MainContent = ({ children }) => {
  return (
    <AdminLayoutWrapper>
        {children}
    </AdminLayoutWrapper>
  );
};
// Main Interactive US Map Component
const InteractiveUSMapComponent = () => {
  const [selectedState, setSelectedState] = useState('');
  const [showCountyModal, setShowCountyModal] = useState(false);
  const [showRecordingForm, setShowRecordingForm] = useState(false);
  const [selectedCounty, setSelectedCounty] = useState('');

  const handleStateClick = (stateKey) => {
    setSelectedState(stateKey);
    setShowCountyModal(true);
  };

  const handleCountySelect = (countyName) => {
    setSelectedCounty(countyName);
    setShowCountyModal(false);
    setShowRecordingForm(true);
  };

  const handleCreateRecording = (recordingData) => {
    console.log('Creating recording with data:', recordingData);
    // Reset selections
    setSelectedState('');
    setSelectedCounty('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-gray-900 to-black p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <CrownIcon size={32} className="text-yellow-500" />
            <h1 className="text-2xl font-bold text-white">William Walker</h1>
          </div>
          <h2 className="text-xl font-semibold text-white">State Leads Map</h2>
        </div>

        {/* Map Container */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="text-center mb-6">
            <h3 className="text-lg font-semibold text-white mb-2">Select a State</h3>
            <p className="text-gray-400">Click on any state to view available counties and lead counts</p>
          </div>

          {/* US Map */}
          <div className="flex justify-center">
            <USMapSVG 
              onStateClick={handleStateClick}
              selectedState={selectedState}
            />
          </div>

          {/* Legend */}
          <div className="mt-6 flex justify-center">
            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="text-white font-semibold mb-2">Available States</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-yellow-600 rounded"></div>
                  <span className="text-gray-300">Texas (TX)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-yellow-600 rounded"></div>
                  <span className="text-gray-300">California (CA)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-yellow-600 rounded"></div>
                  <span className="text-gray-300">Florida (FL)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-yellow-600 rounded"></div>
                  <span className="text-gray-300">New York (NY)</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-3">How to Use</h3>
          <ol className="text-gray-300 space-y-2">
            <li>1. Click on any highlighted state on the map</li>
            <li>2. Select a county from the list with available leads</li>
            <li>3. Create a recording campaign for that county</li>
            <li>4. The system will generate calls for all leads in that area</li>
          </ol>
        </div>

        {/* County Modal */}
        <CountyModal
          isOpen={showCountyModal}
          stateName={selectedState ? stateData[selectedState]?.name : ''}
          counties={selectedState ? stateData[selectedState]?.counties || [] : []}
          onClose={() => setShowCountyModal(false)}
          onSelectCounty={handleCountySelect}
        />

        {/* Create Recording Form */}
        {showRecordingForm && (
          <CreateRecordingForm
            county={selectedCounty}
            state={selectedState ? stateData[selectedState]?.name : ''}
            onClose={() => setShowRecordingForm(false)}
            onSubmit={handleCreateRecording}
          />
        )}
      </div>
    </div>
  );
};

const InteractiveUSMap = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen bg-gray-900">
        <MainContent>
          <InteractiveUSMapComponent />
        </MainContent>
      
    </div>
  );
};


export default InteractiveUSMap;